<script setup>
import {
  computed,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStoreGetters } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from 'dashboard/components-next/polaris/ui/tabs';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from 'dashboard/components-next/polaris/ui/empty';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';
import RowMenu from 'dashboard/components-next/polaris/cards/RowMenu.vue';
import { View16 } from '@carbon/icons-vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import DataImportsAPI from 'dashboard/api/dataImports';
import NewImportDialog from './NewImportDialog.vue';
import { importSourceFor } from './importSources';
import {
  POLL_INTERVAL_MS,
  formatDate,
  formatStatus,
  importedCount,
  isActiveImport,
  isActiveIntercomImport,
  statusDotClass,
} from './importStatus';

const { t } = useI18n();
const getters = useStoreGetters();
const router = useRouter();

const dataImports = ref([]);
const isLoading = ref(true);
const isRefreshing = ref(false);
const isPolling = ref(false);
const showImportDrawer = ref(false);
const activeTab = ref('import');
let pollTimer;
let isPageActive = false;

const accountId = getters.getCurrentAccountId;

const tabs = computed(() => [
  { key: 'import', label: t('DATA_IMPORTS.TABS.IMPORT') },
  { key: 'export', label: t('DATA_IMPORTS.TABS.EXPORT') },
]);

const hasActiveImport = computed(() => dataImports.value.some(isActiveImport));
const hasActiveIntercomImport = computed(() =>
  dataImports.value.some(isActiveIntercomImport)
);

const dataImportRoute = dataImport => ({
  name: 'settings_data_import_show',
  params: { accountId: accountId.value, dataImportId: dataImport.id },
});

const importTypesFor = dataImport =>
  dataImport.import_types?.length
    ? dataImport.import_types
    : [dataImport.data_type];

const importTypeLabel = dataImport =>
  importTypesFor(dataImport)
    .map(type => {
      if (type === 'contacts') return t('DATA_IMPORTS.TYPES.CONTACTS');
      if (type === 'conversations') {
        return t('DATA_IMPORTS.TYPES.CONVERSATIONS');
      }
      return type;
    })
    .join(', ');

const fetchImports = async () => {
  const response = await DataImportsAPI.get();
  dataImports.value = response.data.payload || [];
};

const stopPolling = () => {
  if (!pollTimer) return;

  window.clearInterval(pollTimer);
  pollTimer = null;
};

const refreshImportsInBackground = async () => {
  if (
    !isPageActive ||
    isPolling.value ||
    !hasActiveImport.value ||
    document.hidden
  ) {
    return;
  }

  isPolling.value = true;
  try {
    await fetchImports();
  } finally {
    isPolling.value = false;
    if (!hasActiveImport.value) stopPolling();
  }
};

const startPolling = () => {
  stopPolling();
  if (!isPageActive || !hasActiveImport.value) return;

  pollTimer = window.setInterval(refreshImportsInBackground, POLL_INTERVAL_MS);
};

const refresh = async ({ showLoader = true } = {}) => {
  if (showLoader) isLoading.value = true;
  else isRefreshing.value = true;

  try {
    await fetchImports();
  } finally {
    isLoading.value = false;
    isRefreshing.value = false;
    if (isPageActive) {
      if (hasActiveImport.value && !pollTimer) startPolling();
      if (!hasActiveImport.value) stopPolling();
    }
  }
};

const openImport = dataImport => {
  router.push(dataImportRoute(dataImport));
};

const openImportDrawer = () => {
  if (!hasActiveIntercomImport.value) showImportDrawer.value = true;
};

const onImportCreated = dataImportId => {
  showImportDrawer.value = false;
  router.push({
    name: 'settings_data_import_show',
    params: { accountId: accountId.value, dataImportId },
  });
};

const onTabChanged = key => {
  activeTab.value = key;
};

// Phase 9.9: the list of imports in the kit's table; the name opens the import (the whole row did before), and
// so does View in its ⋮ menu.
const tableHeaders = computed(() => [
  t('DATA_IMPORTS.TABLE.NAME'),
  t('DATA_IMPORTS.TABLE.STATUS'),
  t('DATA_IMPORTS.TABLE.TYPE'),
  t('DATA_IMPORTS.TABLE.IMPORTED'),
  t('DATA_IMPORTS.TABLE.CREATED'),
  t('SETTINGS_TABLE.ACTIONS'),
]);
const viewMenuItems = computed(() => [
  { label: t('DATA_IMPORTS.TABLE.VIEW'), value: 'view', icon: View16 },
]);

const handleVisibilityChange = () => {
  if (isPageActive && !document.hidden && hasActiveImport.value) {
    refreshImportsInBackground();
  }
};

onActivated(async () => {
  isPageActive = true;
  await refresh();
  if (!isPageActive) return;

  startPolling();
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onDeactivated(() => {
  isPageActive = false;
  stopPolling();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

onBeforeUnmount(() => {
  isPageActive = false;
  stopPolling();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>

<!-- Phase 9.9 (bloco I): Import | Export in the kit's line Tabs, the imports in the kit's table (source mark,
     name, status with its dot, type, imported, date), the kit's Empty (with "Import") and Export still "coming
     soon" in the Empty. Polling, refresh and the new-import dialog are unchanged. -->
<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('DATA_IMPORTS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('DATA_IMPORTS.HEADER')"
        :description="$t('DATA_IMPORTS.DESCRIPTION')"
      >
        <template #tabs>
          <Tabs :model-value="activeTab" @update:model-value="onTabChanged">
            <TabsList variant="line" :aria-label="$t('DATA_IMPORTS.HEADER')">
              <TabsTrigger v-for="tab in tabs" :key="tab.key" :value="tab.key">
                {{ tab.label }}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </template>
        <template v-if="activeTab === 'import' && dataImports.length" #count>
          {{ $t('DATA_IMPORTS.TABLE.COUNT', { count: dataImports.length }) }}
        </template>
        <template v-if="activeTab === 'import'" #actions>
          <span
            v-if="hasActiveImport"
            class="hidden items-center gap-1.5 type-polaris-caption text-polaris-muted-foreground sm:inline-flex"
          >
            <span class="size-2 animate-pulse rounded-full bg-n-teal-9" />
            {{
              $t('DATA_IMPORTS.MONITOR.LIVE', {
                seconds: POLL_INTERVAL_MS / 1000,
              })
            }}
          </span>
          <Button
            ghost
            slate
            size="sm"
            icon="i-lucide-refresh-cw"
            :is-loading="isRefreshing"
            :aria-label="$t('DATA_IMPORTS.MONITOR.REFRESH')"
            :title="$t('DATA_IMPORTS.MONITOR.REFRESH')"
            @click="refresh({ showLoader: false })"
          />
          <Button
            size="sm"
            :label="$t('DATA_IMPORTS.TABLE.NEW_IMPORT')"
            :disabled="hasActiveIntercomImport"
            @click="openImportDrawer"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <p
        v-if="activeTab === 'import' && hasActiveIntercomImport"
        class="type-polaris-body text-polaris-muted-foreground"
      >
        {{ $t('DATA_IMPORTS.DRAWER.ACTIVE_IMPORT') }}
      </p>
      <Empty
        v-if="activeTab === 'export'"
        class="border border-polaris-border md:p-8"
      >
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icon icon="i-lucide-upload" class="size-4" />
          </EmptyMedia>
          <EmptyTitle>{{ $t('DATA_IMPORTS.EXPORT.TITLE') }}</EmptyTitle>
          <EmptyDescription>
            {{ $t('DATA_IMPORTS.EXPORT.DESCRIPTION') }}
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Badge variant="outline">
            {{ $t('DATA_IMPORTS.EXPORT.COMING_SOON') }}
          </Badge>
        </EmptyContent>
      </Empty>

      <Empty
        v-else-if="!dataImports.length"
        class="border border-polaris-border md:p-8"
      >
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icon icon="i-lucide-database" class="size-4" />
          </EmptyMedia>
          <EmptyTitle>{{ $t('DATA_IMPORTS.TABLE.EMPTY') }}</EmptyTitle>
          <EmptyDescription>
            {{ $t('DATA_IMPORTS.TABLE.EMPTY_DESCRIPTION') }}
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button
            size="sm"
            outline
            :label="$t('DATA_IMPORTS.TABLE.NEW_IMPORT')"
            @click="openImportDrawer"
          />
        </EmptyContent>
      </Empty>

      <BaseTable
        v-else
        :headers="tableHeaders"
        :items="dataImports"
        pin-last-column
      >
        <template #row="{ items }">
          <BaseTableRow
            v-for="dataImport in items"
            :key="dataImport.id"
            :item="dataImport"
          >
            <BaseTableCell wrap>
              <span class="flex min-w-0 items-center gap-3">
                <img
                  v-if="importSourceFor(dataImport).icon"
                  v-tooltip.top="importSourceFor(dataImport).label"
                  :src="importSourceFor(dataImport).icon"
                  alt=""
                  class="size-8 shrink-0 rounded-polaris-sm border border-solid border-polaris-border object-contain"
                />
                <span
                  v-else
                  v-tooltip.top="importSourceFor(dataImport).label"
                  class="grid size-8 shrink-0 place-items-center rounded-polaris-sm border border-solid border-polaris-border bg-polaris-muted"
                >
                  <Icon
                    :icon="importSourceFor(dataImport).iconClass"
                    class="size-4"
                  />
                </span>
                <router-link
                  :to="dataImportRoute(dataImport)"
                  class="min-w-0 text-polaris-foreground hover:underline"
                >
                  {{ dataImport.name || $t('DATA_IMPORTS.TABLE.UNNAMED') }}
                </router-link>
              </span>
            </BaseTableCell>
            <BaseTableCell>
              <span class="inline-flex items-center gap-1.5">
                <span
                  class="size-2 rounded-full"
                  :class="[
                    statusDotClass(dataImport.status),
                    { 'animate-pulse': isActiveImport(dataImport) },
                  ]"
                />
                <span class="capitalize">
                  {{ formatStatus(dataImport.status) }}
                </span>
              </span>
            </BaseTableCell>
            <BaseTableCell>{{ importTypeLabel(dataImport) }}</BaseTableCell>
            <BaseTableCell class="tabular-nums">
              {{ importedCount(dataImport) }}
            </BaseTableCell>
            <BaseTableCell>
              {{ formatDate(dataImport.created_at) }}
            </BaseTableCell>
            <BaseTableCell actions>
              <RowMenu
                :label="
                  $t('SETTINGS_TABLE.ROW_ACTIONS', {
                    name: dataImport.name || $t('DATA_IMPORTS.TABLE.UNNAMED'),
                  })
                "
                :items="viewMenuItems"
                @action="openImport(dataImport)"
              />
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>
  </SettingsLayout>

  <NewImportDialog
    :show="showImportDrawer"
    :has-active-import="hasActiveIntercomImport"
    @close="showImportDrawer = false"
    @created="onImportCreated"
  />
</template>
